import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import Lenis from "lenis";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useReducedMotion } from "motion/react";

gsap.registerPlugin(ScrollTrigger);

const LenisContext = createContext<Lenis | null>(null);

export function LenisProvider({ children }: { children: ReactNode }) {
  const [lenis, setLenis] = useState<Lenis | null>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return;

    const instance = new Lenis({
      duration: 1.1,
      easing: (t: number) => 1 - Math.pow(1 - t, 3),
    });

    // The hero sections' GSAP ScrollTriggers need to hear about every Lenis
    // tick, and GSAP's ticker becomes the single driver for Lenis's own raf
    // loop — one shared clock instead of two independent requestAnimationFrame
    // loops racing each other frame to frame.
    instance.on("scroll", ScrollTrigger.update);
    function onTick(time: number) {
      instance.raf(time * 1000);
    }
    gsap.ticker.add(onTick);
    gsap.ticker.lagSmoothing(0);

    setLenis(instance);
    return () => {
      gsap.ticker.remove(onTick);
      instance.destroy();
      setLenis(null);
    };
  }, [reduced]);

  return <LenisContext.Provider value={lenis}>{children}</LenisContext.Provider>;
}

export function useLenis() {
  return useContext(LenisContext);
}

// Scrolls to an element by id, honoring its scroll-margin-top (used to clear
// the fixed nav) the same way native scrollIntoView does — Lenis's own
// scrollTo only takes an explicit offset, so we read the CSS value ourselves.
export function scrollToId(lenis: Lenis | null, id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  if (lenis) {
    const margin = parseFloat(getComputedStyle(el).scrollMarginTop || "0");
    lenis.scrollTo(el, { offset: -margin });
  } else {
    el.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth", block: "start" });
  }
}
